// Ce que l'interface DIT, selon à qui elle parle ET dans quelle langue.
//
// POURQUOI PAS `labels.ts`. Celui-ci sert les composants de `packages/ui`, il
// est engendré depuis le dictionnaire français de l'extension, et il dit la
// même chose aux deux apps : c'est tout son intérêt. Ici c'est l'inverse, on
// veut deux phrases différentes pour la même chose.
//
// POURQUOI CE N'EST PAS QU'UN RENOMMAGE. Un business manager ARBITRE entre des
// gens : il a une pile de décisions, il propose un profil, il monte un dossier.
// Un particulier n'arbitre rien, il regarde ses opportunités et il postule. Les
// mots suivent une différence réelle, ils ne la créent pas. Là où seul le mot
// change, il est ici ; là où l'écran change de nature, c'est dans l'écran.
//
// DEUX AXES, PAS UN SEUL (depuis le 2026-08-26). `formule` choisit LEQUEL des
// deux publics on sert, `lang` choisit dans quelle langue. Chaque entrée est
// donc `{ fr: [console, radar], en: [console, radar] }` plutôt que le tuple
// `[console, radar]` d'avant : ajouter une langue, c'est ajouter une clé à cet
// objet, pas redessiner la forme.

import { useFormule, type Formule } from './formule'
import { useLang, type Lang } from './lang'

type Paire = readonly [string, string]

/** Une entrée par notion, dans l'ordre console puis Radar, pour chaque langue. */
const MOTS = {
  // Navigation
  'nav.decisions': {
    fr: ['Décisions à prendre', 'Radar'],
    en: ['Decisions to make', 'Radar'],
    nl: ['Te nemen beslissingen', 'Radar'],
    it: ['Decisioni da prendere', 'Radar'],
    es: ['Decisiones pendientes', 'Radar'],
  },
  'nav.suivi': {
    fr: ['Suivi', 'Mes offres'],
    en: ['Tracking', 'My jobs'],
    nl: ['Opvolging', 'Mijn vacatures'],
    it: ['Monitoraggio', 'Le mie offerte'],
    es: ['Seguimiento', 'Mis ofertas'],
  },
  'nav.veille': {
    fr: ['Veille', 'Toutes les offres'],
    en: ['Watch', 'All postings'],
    nl: ['Opvolging van bronnen', 'Alle vacatures'],
    it: ['Osservatorio', 'Tutte le offerte'],
    es: ['Vigilancia', 'Todas las ofertas'],
  },
  'nav.aide': {
    fr: ['Prise en main', 'Aide et extension'],
    en: ['Getting started', 'Help and extension'],
    nl: ['Aan de slag', 'Hulp en extensie'],
    it: ['Per iniziare', 'Aiuto ed estensione'],
    es: ['Primeros pasos', 'Ayuda y extensión'],
  },
  'nav.sources': {
    fr: ['Sources', 'Employeurs'],
    en: ['Sources', 'Employers'],
    nl: ['Bronnen', 'Werkgevers'],
    it: ['Fonti', 'Datori di lavoro'],
    es: ['Fuentes', 'Empleadores'],
  },
  'nav.profil': {
    fr: ['Vivier', 'Mon CV'],
    en: ['Bench', 'My CV'],
    nl: ['Pool', 'Mijn cv'],
    it: ['Vivaio', 'Il mio CV'],
    es: ['Cantera', 'Mi CV'],
  },
  'nav.sousTitre': {
    fr: ['Consultance', 'Radar'],
    en: ['Consultancy', 'Radar'],
    nl: ['Consultancy', 'Radar'],
    it: ['Consulenza', 'Radar'],
    es: ['Consultoría', 'Radar'],
  },

  // LA MACHINE D'ÉTATS DU TRI (issue #77). Un même statut portait jusqu'à
  // trois noms en Radar selon l'écran (« Later » sur les opportunités, « To
  // revisit » sur la fiche, « Set aside » dans le suivi). Chaque écran lit
  // désormais ces quatre entrées, et `offerStatus` aussi. Les clés en base
  // (`todo`, `later`, `skip`) ne changent pas : on renomme l'affichage.
  // « Je postule » plutôt que « Suivre cette offre » : « suivre » désigne
  // déjà le geste sur un site.
  'etat.todo': {
    fr: ['À décider', 'Nouvelle'],
    en: ['To decide', 'New'],
    nl: ['Te beslissen', 'Nieuw'],
    it: ['Da decidere', 'Nuova'],
    es: ['Por decidir', 'Nueva'],
  },
  'etat.later': {
    fr: ['À revoir', 'Plus tard'],
    en: ['To revisit', 'Later'],
    nl: ['Later bekijken', 'Later'],
    it: ['Da rivedere', 'Più tardi'],
    es: ['Por revisar', 'Más tarde'],
  },
  'etat.skip': {
    fr: ['Écartée', 'Pas pour moi'],
    en: ['Set aside', 'Not for me'],
    nl: ['Opzijgezet', 'Niet voor mij'],
    it: ['Scartata', 'Non fa per me'],
    es: ['Descartada', 'No es para mí'],
  },
  'etat.postuler': {
    fr: ['Proposer', 'Je postule'],
    en: ['Put forward', "I'm applying"],
    nl: ['Voorstellen', 'Ik solliciteer'],
    it: ['Proporre', 'Mi candido'],
    es: ['Proponer', 'Me postulo'],
  },
  'pile.ecartees': {
    fr: ['Écartées', 'Pas pour moi'],
    en: ['Skipped', 'Not for me'],
    nl: ['Opzijgezet', 'Niet voor mij'],
    it: ['Scartate', 'Non fa per me'],
    es: ['Descartadas', 'No es para mí'],
  },

  // Suivi
  'suivi.naissance': {
    fr: [
      'Une candidature naît quand tu proposes un profil sur une offre, depuis ',
      'Une candidature naît quand vous dites « Je postule » sur une offre, depuis ',
    ],
    en: [
      'An application is born when you put a profile forward on a posting, from ',
      "An application is born when you say \"I'm applying\" on a posting, from ",
    ],
    nl: ['Een sollicitatie ontstaat wanneer je een profiel voorstelt voor een vacature, vanuit ', 'Een sollicitatie ontstaat wanneer u “Ik solliciteer” zegt bij een vacature, vanuit '],
    it: ['Una candidatura nasce quando proponi un profilo su un’offerta, da ', 'Una candidatura nasce quando dici “Mi candido” su un’offerta, da '],
    es: ['Una candidatura nace cuando propones un perfil para una oferta, desde ', 'Una candidatura nace cuando dices “Me postulo” en una oferta, desde '],
  },
  'suivi.reserve': {
    fr: ['À revoir', 'Plus tard'],
    en: ['To revisit', 'Later'],
    nl: ['Later bekijken', 'Later'],
    it: ['Da rivedere', 'Più tardi'],
    es: ['Por revisar', 'Más tarde'],
  },
  'suivi.reserveAide': {
    fr: ['Mises de côté, personne de dispo sur le moment', 'Gardées pour plus tard, rien de décidé'],
    en: ['Set aside, nobody available right now', 'Kept for later, nothing decided'],
    nl: ['Opzijgelegd, op dit moment niemand beschikbaar', 'Bewaard voor later, nog niets beslist'],
    it: ['Messe da parte, nessuno disponibile al momento', 'Tenute per più tardi, niente di deciso'],
    es: ['Apartadas, nadie disponible por ahora', 'Guardadas para más tarde, nada decidido'],
  },
  'suivi.closes': {
    fr: ['Clôturées', 'Terminées'],
    en: ['Closed', 'Finished'],
    nl: ['Afgesloten', 'Afgerond'],
    it: ['Chiuse', 'Concluse'],
    es: ['Cerradas', 'Terminadas'],
  },
  'suivi.sansDossier': {
    fr: ['sans dossier', 'CV non adapté'],
    en: ['no file', 'CV not tailored'],
    nl: ['zonder dossier', 'cv niet aangepast'],
    it: ['senza dossier', 'CV non adattato'],
    es: ['sin expediente', 'CV sin adaptar'],
  },
  'suivi.remettre': {
    fr: ['Remettre dans la pile', 'Annuler « Plus tard »'],
    en: ['Put back in the pile', 'Undo “Later”'],
    nl: ['Terug op de stapel', '“Later” ongedaan maken'],
    it: ['Rimetti nella pila', 'Annulla “Più tardi”'],
    es: ['Volver a la pila', 'Deshacer “Más tarde”'],
  },
  'suivi.placementsCount': {
    fr: ['placement(s)', 'aboutie(s)'],
    en: ['placement(s)', 'successful'],
    nl: ['plaatsing(en)', 'geslaagd'],
    it: ['inserimento/i', 'riuscita/e'],
    es: ['colocación(es)', 'conseguida(s)'],
  },

  // Écran des décisions / opportunités
  'pile.titre': {
    fr: ['Décisions à prendre', 'Mes opportunités'],
    en: ['Decisions to make', 'My opportunities'],
    nl: ['Te nemen beslissingen', 'Mijn kansen'],
    it: ['Decisioni da prendere', 'Le mie opportunità'],
    es: ['Decisiones pendientes', 'Mis oportunidades'],
  },
  'pile.vide': {
    fr: ['La pile est vide', 'Aucune opportunité pour le moment'],
    en: ['The pile is empty', 'No opportunity right now'],
    nl: ['De stapel is leeg', 'Op dit moment geen kansen'],
    it: ['La pila è vuota', 'Nessuna opportunità al momento'],
    es: ['La pila está vacía', 'Ninguna oportunidad por ahora'],
  },
  'pile.videAide': {
    fr: [
      "Rien n'attend de décision. Les nouvelles offres arrivent après le run de la nuit.",
      "Rien ne correspond à votre profil pour l'instant. Les nouvelles offres arrivent chaque nuit.",
    ],
    en: [
      'Nothing is waiting on a decision. New postings arrive after the nightly run.',
      "Nothing matches your profile right now. New postings arrive every night.",
    ],
    nl: ['Er wacht niets op een beslissing. Nieuwe vacatures komen na de nachtelijke run.', 'Op dit moment past niets bij uw profiel. Elke nacht komen er nieuwe vacatures bij.'],
    it: ['Niente attende una decisione. Le nuove offerte arrivano dopo l’elaborazione notturna.', 'Per ora niente corrisponde al tuo profilo. Ogni notte arrivano nuove offerte.'],
    es: ['Nada espera una decisión. Las nuevas ofertas llegan tras el proceso nocturno.', 'Por ahora nada encaja con tu perfil. Cada noche llegan ofertas nuevas.'],
  },

  // Offre
  'offre.candidats': {
    fr: ['Classement des profils', 'Mon score'],
    en: ['Profile ranking', 'My score'],
    nl: ['Rangschikking van de profielen', 'Mijn score'],
    it: ['Classifica dei profili', 'Il mio punteggio'],
    es: ['Clasificación de perfiles', 'Mi puntuación'],
  },
  'offre.aucunCandidat': {
    fr: [
      'Aucun profil du vivier ne ressort sur cette offre.',
      "Votre profil n'a pas encore été comparé à cette offre.",
    ],
    en: [
      'No profile from the bench stands out on this posting.',
      "Your profile hasn't been compared to this posting yet.",
    ],
    nl: ['Geen enkel profiel uit de pool springt eruit voor deze vacature.', 'Uw profiel is nog niet met deze vacature vergeleken.'],
    it: ['Nessun profilo del vivaio emerge su questa offerta.', 'Il tuo profilo non è ancora stato confrontato con questa offerta.'],
    es: ['Ningún perfil de la cantera destaca en esta oferta.', 'Tu perfil aún no se ha comparado con esta oferta.'],
  },
  'offre.evaluer': {
    fr: ['Évaluer un autre profil', 'Évaluer mon profil'],
    en: ['Evaluate another profile', 'Evaluate my profile'],
    nl: ['Een ander profiel beoordelen', 'Mijn profiel beoordelen'],
    it: ['Valuta un altro profilo', 'Valuta il mio profilo'],
    es: ['Evaluar otro perfil', 'Evaluar mi perfil'],
  },
  'offre.proposer': {
    fr: ['Proposer ce profil', 'Postuler'],
    en: ['Put this profile forward', 'Apply'],
    nl: ['Dit profiel voorstellen', 'Solliciteren'],
    it: ['Proponi questo profilo', 'Candidati'],
    es: ['Proponer este perfil', 'Postularme'],
  },

  // Dossier
  'dossier.nom': {
    fr: ['Dossier', 'Ma candidature'],
    en: ['File', 'My application'],
    nl: ['Dossier', 'Mijn sollicitatie'],
    it: ['Dossier', 'La mia candidatura'],
    es: ['Expediente', 'Mi candidatura'],
  },
  // Le libellé long ne tient pas dans une puce de ligne de suivi.
  'dossier.court': {
    fr: ['Dossier', 'CV adapté'],
    en: ['File', 'Tailored CV'],
    nl: ['Dossier', 'Aangepast cv'],
    it: ['Dossier', 'CV adattato'],
    es: ['Expediente', 'CV adaptado'],
  },
  'dossier.generer': {
    fr: ['Générer le dossier', 'Adapter mon CV'],
    en: ['Generate the file', 'Tailor my CV'],
    nl: ['Dossier aanmaken', 'Mijn cv aanpassen'],
    it: ['Genera il dossier', 'Adatta il mio CV'],
    es: ['Generar el expediente', 'Adaptar mi CV'],
  },

  // Suivi
  'suivi.titre': {
    fr: ['Suivi des candidatures', 'Mes offres'],
    en: ['Application tracking', 'My jobs'],
    nl: ['Opvolging van sollicitaties', 'Mijn vacatures'],
    it: ['Monitoraggio delle candidature', 'Le mie offerte'],
    es: ['Seguimiento de candidaturas', 'Mis ofertas'],
  },
  'suivi.vide': {
    fr: ['Aucune candidature en cours', "Vous n'avez postulé nulle part pour l'instant"],
    en: ['No application in progress', "You haven't applied anywhere yet"],
    nl: ['Geen lopende sollicitatie', 'U hebt nog nergens gesolliciteerd'],
    it: ['Nessuna candidatura in corso', 'Non ti sei ancora candidato da nessuna parte'],
    es: ['Ninguna candidatura en curso', 'Todavía no te has postulado a ninguna'],
  },
  'suivi.retirer': {
    fr: [
      'Retirer cette candidature du suivi (elle reste au journal)',
      "Retirer de mes candidatures (l'historique est conservé)",
    ],
    en: [
      'Remove this application from tracking (it stays in the log)',
      'Remove from my applications (the history is kept)',
    ],
    nl: ['Deze sollicitatie uit de opvolging halen (ze blijft in het logboek)', 'Uit mijn sollicitaties halen (de geschiedenis blijft bewaard)'],
    it: ['Togli questa candidatura dal monitoraggio (resta nel registro)', 'Togli dalle mie candidature (lo storico resta)'],
    es: ['Quitar esta candidatura del seguimiento (queda en el registro)', 'Quitar de mis candidaturas (se conserva el historial)'],
  },

  // Fiche profil
  'profil.cv': {
    fr: ['Son CV maître', 'Mon CV'],
    en: ['Their master CV', 'My CV'],
    nl: ['Zijn of haar master-cv', 'Mijn cv'],
    it: ['Il suo CV principale', 'Il mio CV'],
    es: ['Su CV principal', 'Mi CV'],
  },
  'profil.cvVide': {
    fr: [
      'Aucun profil structuré pour ce consultant. Importe son CV.',
      'Aucun profil structuré. Déposez votre CV pour commencer.',
    ],
    en: [
      'No structured profile for this consultant. Import their CV.',
      'No structured profile. Upload your CV to get started.',
    ],
    nl: ['Geen gestructureerd profiel voor deze consultant. Importeer het cv.', 'Geen gestructureerd profiel. Laad uw cv op om te beginnen.'],
    it: ['Nessun profilo strutturato per questo consulente. Importa il suo CV.', 'Nessun profilo strutturato. Carica il tuo CV per iniziare.'],
    es: ['Ningún perfil estructurado para este consultor. Importa su CV.', 'Ningún perfil estructurado. Sube tu CV para empezar.'],
  },
  'profil.importer': {
    fr: ['Réimporter le CV', 'Mettre à jour mon CV'],
    en: ['Re-import the CV', 'Update my CV'],
    nl: ['Cv opnieuw importeren', 'Mijn cv bijwerken'],
    it: ['Reimporta il CV', 'Aggiorna il mio CV'],
    es: ['Volver a importar el CV', 'Actualizar mi CV'],
  },
  'profil.introuvable': {
    fr: ['Consultant introuvable', 'Profil introuvable'],
    en: ['Consultant not found', 'Profile not found'],
    nl: ['Consultant niet gevonden', 'Profiel niet gevonden'],
    it: ['Consulente non trovato', 'Profilo non trovato'],
    es: ['Consultor no encontrado', 'Perfil no encontrado'],
  },
  'profil.moyenneVivier': {
    fr: ['Vivier (moy.)', 'Moyenne de mes offres'],
    en: ['Bench (avg.)', 'Average of my postings'],
    nl: ['Pool (gem.)', 'Gemiddelde van mijn vacatures'],
    it: ['Vivaio (media)', 'Media delle mie offerte'],
    es: ['Cantera (media)', 'Media de mis ofertas'],
  },
  'profil.aucunDossier': {
    fr: ['Aucun dossier généré pour ce consultant.', "Aucun CV adapté pour l'instant."],
    en: ['No file generated for this consultant.', 'No tailored CV yet.'],
    nl: ['Geen dossier aangemaakt voor deze consultant.', 'Nog geen aangepast cv.'],
    it: ['Nessun dossier generato per questo consulente.', 'Ancora nessun CV adattato.'],
    es: ['Ningún expediente generado para este consultor.', 'Todavía ningún CV adaptado.'],
  },
  'profil.aucuneOffre': {
    fr: [
      "Pas encore d'offre évaluée pour ce profil.",
      "Aucune offre comparée à votre profil pour l'instant.",
    ],
    en: [
      'No posting evaluated for this profile yet.',
      "No posting compared to your profile yet.",
    ],
    nl: ['Nog geen vacature beoordeeld voor dit profiel.', 'Nog geen vacature met uw profiel vergeleken.'],
    it: ['Ancora nessuna offerta valutata per questo profilo.', 'Ancora nessuna offerta confrontata con il tuo profilo.'],
    es: ['Todavía ninguna oferta evaluada para este perfil.', 'Todavía ninguna oferta comparada con tu perfil.'],
  },

  'profil.cvMaitre': {
    fr: ['CV maître', 'Mon CV'],
    en: ['Master CV', 'My CV'],
    nl: ['Master-cv', 'Mijn cv'],
    it: ['CV principale', 'Il mio CV'],
    es: ['CV principal', 'Mi CV'],
  },
  'profil.chiffres': {
    fr: ['Ses chiffres', 'Mes chiffres'],
    en: ['Their numbers', 'My numbers'],
    nl: ['Zijn of haar cijfers', 'Mijn cijfers'],
    it: ['I suoi numeri', 'I miei numeri'],
    es: ['Sus cifras', 'Mis cifras'],
  },
  'profil.scoreMoyen': {
    fr: ['Score moyen sur ses offres', 'Mon score moyen'],
    en: ['Average score on their postings', 'My average score'],
    nl: ['Gemiddelde score op de vacatures', 'Mijn gemiddelde score'],
    it: ['Punteggio medio sulle sue offerte', 'Il mio punteggio medio'],
    es: ['Puntuación media en sus ofertas', 'Mi puntuación media'],
  },
  'profil.positionnements': {
    fr: ['Positionnements', 'Candidatures'],
    en: ['Placements put forward', 'Applications'],
    nl: ['Voorstellen', 'Sollicitaties'],
    it: ['Proposte', 'Candidature'],
    es: ['Propuestas', 'Candidaturas'],
  },
  'profil.meilleur': {
    fr: ['Meilleur match en cours', 'Mon meilleur score'],
    en: ['Best current match', 'My best score'],
    nl: ['Beste lopende match', 'Mijn beste score'],
    it: ['Miglior corrispondenza attuale', 'Il mio miglior punteggio'],
    es: ['Mejor coincidencia actual', 'Mi mejor puntuación'],
  },
  'profil.offresEvaluees': {
    fr: ['Offres évaluées', 'Offres comparées à mon CV'],
    en: ['Postings evaluated', 'Postings compared to my CV'],
    nl: ['Beoordeelde vacatures', 'Vacatures vergeleken met mijn cv'],
    it: ['Offerte valutate', 'Offerte confrontate con il mio CV'],
    es: ['Ofertas evaluadas', 'Ofertas comparadas con mi CV'],
  },
  'profil.cvAdaptes': {
    fr: ['Ses CV adaptés', 'Mes CV adaptés'],
    en: ['Their tailored CVs', 'My tailored CVs'],
    nl: ['Aangepaste cv’s', 'Mijn aangepaste cv’s'],
    it: ['I suoi CV adattati', 'I miei CV adattati'],
    es: ['Sus CV adaptados', 'Mis CV adaptados'],
  },
  'profil.offresQuiCollent': {
    fr: ['Offres qui lui collent', 'Offres qui me correspondent'],
    en: ['Postings that fit them', 'Postings that fit me'],
    nl: ['Vacatures die passen', 'Vacatures die bij mij passen'],
    it: ['Offerte che gli corrispondono', 'Offerte che mi corrispondono'],
    es: ['Ofertas que le encajan', 'Ofertas que me encajan'],
  },
  'profil.historique': {
    fr: ['Historique, le journal des positionnements', 'Mon historique'],
    en: ['History, the log of placements put forward', 'My history'],
    nl: ['Geschiedenis, het logboek van de voorstellen', 'Mijn geschiedenis'],
    it: ['Storico, il registro delle proposte', 'Il mio storico'],
    es: ['Historial, el registro de propuestas', 'Mi historial'],
  },
  'profil.noteReimport': {
    fr: [
      'Réimporter remplace le profil structuré : les scores déjà calculés restent, les prochains partiront du nouveau CV.',
      'Déposer un nouveau CV remplace le profil structuré : les scores déjà calculés restent, les prochains partiront du nouveau CV.',
    ],
    en: [
      'Re-importing replaces the structured profile: scores already computed stay, the next ones will start from the new CV.',
      'Uploading a new CV replaces the structured profile: scores already computed stay, the next ones will start from the new CV.',
    ],
    nl: ['Opnieuw importeren vervangt het gestructureerde profiel: de berekende scores blijven, de volgende vertrekken van het nieuwe cv.', 'Een nieuw cv opladen vervangt het gestructureerde profiel: de berekende scores blijven, de volgende vertrekken van het nieuwe cv.'],
    it: ['Reimportare sostituisce il profilo strutturato: i punteggi già calcolati restano, i prossimi partiranno dal nuovo CV.', 'Caricare un nuovo CV sostituisce il profilo strutturato: i punteggi già calcolati restano, i prossimi partiranno dal nuovo CV.'],
    es: ['Volver a importar sustituye el perfil estructurado: las puntuaciones ya calculadas se quedan, las siguientes partirán del nuevo CV.', 'Subir un nuevo CV sustituye el perfil estructurado: las puntuaciones ya calculadas se quedan, las siguientes partirán del nuevo CV.'],
  },

  // Import de CV
  'import.titre': {
    fr: ['Importer un CV dans le vivier', 'Déposer mon CV'],
    en: ['Import a CV into the bench', 'Upload my CV'],
    nl: ['Een cv in de pool importeren', 'Mijn cv opladen'],
    it: ['Importa un CV nel vivaio', 'Carica il mio CV'],
    es: ['Importar un CV a la cantera', 'Subir mi CV'],
  },
  'import.aide': {
    fr: [
      "Re-déposer le CV d'une personne déjà présente, au même nom, met son profil à jour.",
      'Déposer un nouveau CV remplace le profil actuel.',
    ],
    en: [
      'Re-uploading the CV of someone already on the bench, under the same name, updates their profile.',
      'Uploading a new CV replaces the current profile.',
    ],
    nl: ['Het cv opnieuw opladen van iemand die al in de pool zit, onder dezelfde naam, werkt het profiel bij.', 'Een nieuw cv opladen vervangt het huidige profiel.'],
    it: ['Ricaricare il CV di una persona già presente, con lo stesso nome, ne aggiorna il profilo.', 'Caricare un nuovo CV sostituisce il profilo attuale.'],
    es: ['Volver a subir el CV de alguien que ya está, con el mismo nombre, actualiza su perfil.', 'Subir un nuevo CV sustituye el perfil actual.'],
  },
  // {name} substitué à la main par l'appelant (common.tsx) : mot()/useMots()
  // n'a pas de mécanique de variables, contrairement à useT() (i18n.ts).
  'import.succesImporte': {
    fr: ['{name} importé dans le vivier', 'CV importé'],
    en: ['{name} imported into the bench', 'CV imported'],
    nl: ['{name} geïmporteerd in de pool', 'Cv geïmporteerd'],
    it: ['{name} importato nel vivaio', 'CV importato'],
    es: ['{name} importado a la cantera', 'CV importado'],
  },

  // Veille
  'veille.colonneProfil': {
    fr: ['Meilleur profil', 'Mon score'],
    en: ['Best profile', 'My score'],
    nl: ['Beste profiel', 'Mijn score'],
    it: ['Miglior profilo', 'Il mio punteggio'],
    es: ['Mejor perfil', 'Mi puntuación'],
  },
  'veille.manuelle': {
    fr: ['Saisie manuelle, cahier des charges client', 'Ajouter une offre trouvée ailleurs'],
    en: ['Manual entry, client brief', 'Add a posting found elsewhere'],
    nl: ['Handmatige invoer, briefing van de klant', 'Een elders gevonden vacature toevoegen'],
    it: ['Inserimento manuale, capitolato del cliente', 'Aggiungi un’offerta trovata altrove'],
    es: ['Introducción manual, pliego del cliente', 'Añadir una oferta encontrada en otro sitio'],
  },

  // Configuration
  'config.sousTitre': {
    fr: [
      'Réglages appliqués à la veille, au matching et aux dossiers',
      'Réglages appliqués à la veille, au matching et à vos CV adaptés',
    ],
    en: [
      'Settings applied to the watch, the matching and the files',
      'Settings applied to the watch, the matching and your tailored CVs',
    ],
    nl: ['Instellingen voor de opvolging, de matching en de dossiers', 'Instellingen voor de opvolging, de matching en uw aangepaste cv’s'],
    it: ['Impostazioni applicate all’osservatorio, al matching e ai dossier', 'Impostazioni applicate all’osservatorio, al matching e ai tuoi CV adattati'],
    es: ['Ajustes aplicados a la vigilancia, al matching y a los expedientes', 'Ajustes aplicados a la vigilancia, al matching y a tus CV adaptados'],
  },
  'config.rapprochement': {
    fr: ['Comment les offres trouvent les profils', "Comment les offres arrivent jusqu'à vous"],
    en: ['How postings find profiles', 'How postings reach you'],
    nl: ['Hoe vacatures profielen vinden', 'Hoe vacatures bij u terechtkomen'],
    it: ['Come le offerte trovano i profili', 'Come le offerte arrivano a te'],
    es: ['Cómo las ofertas encuentran los perfiles', 'Cómo las ofertas llegan hasta ti'],
  },
  'config.rapprochementA': {
    fr: [
      "Chaque profil et chaque offre portent une ou plusieurs catégories de métier, proposées automatiquement puis corrigeables à la main. Une offre est évaluée pour tous les profils disponibles qui partagent au moins une de ses catégories, sans plafond : mieux vaut une évaluation de trop qu'un profil oublié.",
      "Votre profil et chaque offre portent une ou plusieurs catégories de métier, proposées automatiquement puis corrigeables à la main. Une offre vous est évaluée dès qu'elle partage au moins une de vos catégories, sans plafond : mieux vaut une évaluation de trop qu'une offre manquée.",
    ],
    en: [
      "Every profile and every posting carry one or more job categories, suggested automatically and editable by hand. A posting is evaluated for every available profile that shares at least one of its categories, with no cap: better one evaluation too many than one profile missed.",
      "Your profile and every posting carry one or more job categories, suggested automatically and editable by hand. A posting is evaluated for you as soon as it shares at least one of your categories, with no cap: better one evaluation too many than one posting missed.",
    ],
    nl: ['Elk profiel en elke vacature krijgen een of meer beroepscategorieën, automatisch voorgesteld en met de hand aan te passen. Een vacature wordt beoordeeld voor alle beschikbare profielen die minstens één categorie delen, zonder plafond: liever een beoordeling te veel dan een vergeten profiel.', 'Uw profiel en elke vacature krijgen een of meer beroepscategorieën, automatisch voorgesteld en met de hand aan te passen. Een vacature wordt voor u beoordeeld zodra ze minstens één van uw categorieën deelt, zonder plafond: liever een beoordeling te veel dan een gemiste vacature.'],
    it: ['Ogni profilo e ogni offerta hanno una o più categorie professionali, proposte automaticamente e modificabili a mano. Un’offerta viene valutata per tutti i profili disponibili che condividono almeno una delle sue categorie, senza limite: meglio una valutazione in più che un profilo dimenticato.', 'Il tuo profilo e ogni offerta hanno una o più categorie professionali, proposte automaticamente e modificabili a mano. Un’offerta viene valutata per te appena condivide almeno una delle tue categorie, senza limite: meglio una valutazione in più che un’offerta persa.'],
    es: ['Cada perfil y cada oferta tienen una o varias categorías profesionales, propuestas automáticamente y corregibles a mano. Una oferta se evalúa para todos los perfiles disponibles que comparten al menos una de sus categorías, sin tope: mejor una evaluación de más que un perfil olvidado.', 'Tu perfil y cada oferta tienen una o varias categorías profesionales, propuestas automáticamente y corregibles a mano. Una oferta se evalúa para ti en cuanto comparte al menos una de tus categorías, sin tope: mejor una evaluación de más que una oferta perdida.'],
  },
  'config.rapprochementB': {
    fr: [
      "Les catégories se modifient sur la fiche d'un profil ou d'une offre. Elles suivent la classification européenne des métiers, tous secteurs confondus.",
      'Les catégories se modifient sur votre profil ou sur une offre. Elles suivent la classification européenne des métiers, tous secteurs confondus.',
    ],
    en: [
      "Categories are edited on a profile's or a posting's page. They follow the European classification of occupations, across every sector.",
      'Categories are edited on your profile or on a posting. They follow the European classification of occupations, across every sector.',
    ],
    nl: ['De categorieën pas je aan op de fiche van een profiel of een vacature. Ze volgen de Europese beroepenclassificatie, voor alle sectoren.', 'De categorieën past u aan op uw profiel of op een vacature. Ze volgen de Europese beroepenclassificatie, voor alle sectoren.'],
    it: ['Le categorie si modificano sulla scheda di un profilo o di un’offerta. Seguono la classificazione europea delle professioni, per tutti i settori.', 'Le categorie si modificano sul tuo profilo o su un’offerta. Seguono la classificazione europea delle professioni, per tutti i settori.'],
    es: ['Las categorías se modifican en la ficha de un perfil o de una oferta. Siguen la clasificación europea de ocupaciones, en todos los sectores.', 'Las categorías se modifican en tu perfil o en una oferta. Siguen la clasificación europea de ocupaciones, en todos los sectores.'],
  },
  'config.seuil': {
    fr: ['Score minimum pour décider', 'Score minimum pour me la proposer'],
    en: ['Minimum score to decide', 'Minimum score to show it to me'],
    nl: ['Minimale score om te beslissen', 'Minimale score om ze mij voor te stellen'],
    it: ['Punteggio minimo per decidere', 'Punteggio minimo per propormela'],
    es: ['Puntuación mínima para decidir', 'Puntuación mínima para proponérmela'],
  },
  'config.seuilTexte': {
    fr: [
      "Les catégories choisissent qui est évalué, avant tout appel à l'IA. Celui-ci choisit ce qui atterrit dans la pile du matin, à partir du score que l'IA a réellement donné.",
      "Les catégories choisissent quelles offres vous sont évaluées, avant tout appel à l'IA. Celui-ci choisit lesquelles remontent dans vos opportunités, à partir du score que l'IA a réellement donné.",
    ],
    en: [
      'Categories decide who gets evaluated, before any AI call. This setting decides what lands in the morning pile, from the score the AI actually gave.',
      'Categories decide which postings get evaluated for you, before any AI call. This setting decides which ones surface in your opportunities, from the score the AI actually gave.',
    ],
    nl: ['De categorieën bepalen wie beoordeeld wordt, vóór elke oproep aan de AI. Deze instelling bepaalt wat op de ochtendstapel belandt, op basis van de score die de AI echt gaf.', 'De categorieën bepalen welke vacatures voor u beoordeeld worden, vóór elke oproep aan de AI. Deze instelling bepaalt welke in uw kansen verschijnen, op basis van de score die de AI echt gaf.'],
    it: ['Le categorie scelgono chi viene valutato, prima di qualsiasi chiamata all’IA. Questa impostazione sceglie cosa finisce nella pila del mattino, in base al punteggio dato davvero dall’IA.', 'Le categorie scelgono quali offerte vengono valutate per te, prima di qualsiasi chiamata all’IA. Questa impostazione sceglie quali compaiono tra le tue opportunità, in base al punteggio dato davvero dall’IA.'],
    es: ['Las categorías eligen quién se evalúa, antes de cualquier llamada a la IA. Este ajuste elige lo que llega a la pila de la mañana, a partir de la puntuación que realmente dio la IA.', 'Las categorías eligen qué ofertas se evalúan para ti, antes de cualquier llamada a la IA. Este ajuste elige cuáles aparecen en tus oportunidades, a partir de la puntuación que realmente dio la IA.'],
  },
  'config.effetRetenues': {
    fr: ['offres à trancher demain matin', 'offres dans mes opportunités demain matin'],
    en: ['postings to decide on tomorrow morning', 'postings in my opportunities tomorrow morning'],
    nl: ['vacatures om morgenochtend te beslissen', 'vacatures in mijn kansen morgenochtend'],
    it: ['offerte da decidere domani mattina', 'offerte tra le mie opportunità domani mattina'],
    es: ['ofertas por decidir mañana por la mañana', 'ofertas en mis oportunidades mañana por la mañana'],
  },
  'config.effetEcartees': {
    fr: ['offres retirées de la pile', 'offres qui resteront en veille'],
    en: ['postings removed from the pile', 'postings that will stay in the watch'],
    nl: ['vacatures van de stapel gehaald', 'vacatures die in de opvolging blijven'],
    it: ['offerte tolte dalla pila', 'offerte che resteranno nell’osservatorio'],
    es: ['ofertas retiradas de la pila', 'ofertas que se quedarán en la vigilancia'],
  },
  'config.seuilNote': {
    fr: [
      "Réglage d'affichage seulement : rien n'est effacé et rien n'est économisé. Les offres écartées gardent leur score, restent dans la veille et redeviennent visibles si tu redescends le curseur.",
      "Réglage d'affichage seulement : rien n'est effacé et rien n'est économisé. Les offres écartées gardent leur score, restent consultables dans toutes les offres et redeviennent visibles si vous redescendez le curseur.",
    ],
    en: [
      'Display setting only: nothing is deleted and nothing is saved. Postings set aside keep their score, stay in the watch, and become visible again if you lower the slider.',
      'Display setting only: nothing is deleted and nothing is saved. Postings set aside keep their score, stay visible under all postings, and become visible again if you lower the slider.',
    ],
    nl: ['Enkel een weergave-instelling: niets wordt gewist en niets wordt bespaard. Opzijgezette vacatures houden hun score, blijven in de opvolging en worden opnieuw zichtbaar als je de schuifregelaar lager zet.', 'Enkel een weergave-instelling: niets wordt gewist en niets wordt bespaard. Opzijgezette vacatures houden hun score, blijven zichtbaar bij alle vacatures en verschijnen opnieuw als u de schuifregelaar lager zet.'],
    it: ['Solo un’impostazione di visualizzazione: niente viene cancellato e niente viene risparmiato. Le offerte scartate tengono il loro punteggio, restano nell’osservatorio e tornano visibili se abbassi il cursore.', 'Solo un’impostazione di visualizzazione: niente viene cancellato e niente viene risparmiato. Le offerte scartate tengono il loro punteggio, restano consultabili tra tutte le offerte e tornano visibili se abbassi il cursore.'],
    es: ['Solo es un ajuste de visualización: no se borra nada y no se ahorra nada. Las ofertas descartadas conservan su puntuación, siguen en la vigilancia y vuelven a ser visibles si bajas el cursor.', 'Solo es un ajuste de visualización: no se borra nada y no se ahorra nada. Las ofertas descartadas conservan su puntuación, siguen consultables en todas las ofertas y vuelven a ser visibles si bajas el cursor.'],
  },
  'config.dossiers': {
    fr: ['Dossiers adaptés', 'Mes CV adaptés'],
    en: ['Tailored files', 'My tailored CVs'],
    nl: ['Aangepaste dossiers', 'Mijn aangepaste cv’s'],
    it: ['Dossier adattati', 'I miei CV adattati'],
    es: ['Expedientes adaptados', 'Mis CV adaptados'],
  },
  'config.fideleAide': {
    fr: [
      "« Fidèle » est le seul niveau où l'IA ne peut rien inventer. C'est ce qui rend l'audit défendable devant un client.",
      "« Fidèle » est le seul niveau où l'IA ne peut rien inventer. Les autres reformulent, et « Audacieux » extrapole : à vous de juger ce que vous assumez en entretien.",
    ],
    en: [
      '"Faithful" is the only level where the AI can\'t invent anything. It\'s what makes the file defensible in front of a client.',
      '"Faithful" is the only level where the AI can\'t invent anything. The others reword, and "Bold" extrapolates: it\'s your call what you\'re comfortable owning at interview.',
    ],
    nl: ['“Getrouw” is het enige niveau waarop de AI niets kan verzinnen. Dat maakt het dossier verdedigbaar bij een klant.', '“Getrouw” is het enige niveau waarop de AI niets kan verzinnen. De andere herformuleren, en “Gedurfd” extrapoleert: u beslist wat u in een gesprek kunt waarmaken.'],
    it: ['“Fedele” è l’unico livello in cui l’IA non può inventare nulla. È ciò che rende il dossier difendibile davanti a un cliente.', '“Fedele” è l’unico livello in cui l’IA non può inventare nulla. Gli altri riformulano, e “Audace” estrapola: sta a te giudicare cosa puoi sostenere al colloquio.'],
    es: ['“Fiel” es el único nivel en el que la IA no puede inventar nada. Es lo que hace defendible el expediente ante un cliente.', '“Fiel” es el único nivel en el que la IA no puede inventar nada. Los demás reformulan, y “Audaz” extrapola: tú decides lo que puedes defender en la entrevista.'],
  },
  'config.signatureAide': {
    fr: [
      'Signe chaque action au journal : qui a généré un dossier, qui a positionné un consultant.',
      'Signe chaque action au journal, et sert de nom sur vos CV adaptés.',
    ],
    en: [
      'Signs every action in the log: who generated a file, who put a consultant forward.',
      'Signs every action in the log, and appears as the name on your tailored CVs.',
    ],
    nl: ['Ondertekent elke actie in het logboek: wie een dossier maakte, wie een consultant voorstelde.', 'Ondertekent elke actie in het logboek en dient als naam op uw aangepaste cv’s.'],
    it: ['Firma ogni azione nel registro: chi ha generato un dossier, chi ha proposto un consulente.', 'Firma ogni azione nel registro, e serve da nome sui tuoi CV adattati.'],
    es: ['Firma cada acción en el registro: quién generó un expediente, quién propuso a un consultor.', 'Firma cada acción en el registro, y sirve de nombre en tus CV adaptados.'],
  },

  // Le bouton « Retour » de la fiche offre. Trois entrées plutôt qu'un motif
  // « Retour {écran} » : le français contracte différemment selon l'écran
  // (« aux décisions », « au suivi », « à la veille »), et une seule phrase à
  // trou aurait forcé un nom d'écran neutre des deux côtés, donc moins bon
  // dans les deux langues.
  'offre.retourDecisions': {
    fr: ['Retour aux décisions', 'Radar'],
    en: ['Back to decisions', 'Radar'],
    nl: ['Terug naar de beslissingen', 'Radar'],
    it: ['Torna alle decisioni', 'Radar'],
    es: ['Volver a las decisiones', 'Radar'],
  },
  'offre.retourSuivi': {
    fr: ['Retour au suivi', 'Mes offres'],
    en: ['Back to tracking', 'My jobs'],
    nl: ['Terug naar de opvolging', 'Mijn vacatures'],
    it: ['Torna al monitoraggio', 'Le mie offerte'],
    es: ['Volver al seguimiento', 'Mis ofertas'],
  },
  'offre.retourVeille': {
    fr: ['Retour à la veille', 'Retour à toutes les offres'],
    en: ['Back to the watch', 'Back to all postings'],
    nl: ['Terug naar de opvolging van bronnen', 'Terug naar alle vacatures'],
    it: ['Torna all’osservatorio', 'Torna a tutte le offerte'],
    es: ['Volver a la vigilancia', 'Volver a todas las ofertas'],
  },
  'offre.retourEmployeur': {
    fr: ["Retour à l'employeur", "Retour à l'employeur"],
    en: ['Back to the employer', 'Back to the employer'],
    nl: ['Terug naar de werkgever', 'Terug naar de werkgever'],
    it: ['Torna al datore di lavoro', 'Torna al datore di lavoro'],
    es: ['Volver al empleador', 'Volver al empleador'],
  },
  'offre.retourProfil': {
    fr: ['Retour au profil', 'Mon CV'],
    en: ['Back to the profile', 'My CV'],
    nl: ['Terug naar het profiel', 'Mijn cv'],
    it: ['Torna al profilo', 'Il mio CV'],
    es: ['Volver al perfil', 'Mi CV'],
  },

  // Panneau d'analyse
  'analyse.vide': {
    fr: ['Sélectionne ou évalue un profil pour voir son analyse détaillée.', 'Aucune analyse pour cette offre.'],
    en: ['Select or evaluate a profile to see its detailed analysis.', 'No analysis for this posting.'],
    nl: ['Selecteer of beoordeel een profiel om de gedetailleerde analyse te zien.', 'Geen analyse voor deze vacature.'],
    it: ['Seleziona o valuta un profilo per vederne l’analisi dettagliata.', 'Nessuna analisi per questa offerta.'],
    es: ['Selecciona o evalúa un perfil para ver su análisis detallado.', 'Ningún análisis para esta oferta.'],
  },
} satisfies Record<string, Record<Lang, Paire>>

export type CleMot = keyof typeof MOTS

/** Hors composant React : quand la formule (et éventuellement la langue) sont
 *  déjà connues. `lang` par défaut à 'fr' pour ne rien changer aux appels
 *  existants qui ignorent encore la langue (cf. Phase 1 du chantier i18n) :
 *  le français reste le comportement d'aujourd'hui tant qu'un appelant ne
 *  passe pas explicitement autre chose. */
export function mot(cle: CleMot, formule: Formule, lang: Lang = 'fr'): string {
  return MOTS[cle][lang][formule === 'radar' ? 1 : 0]
}

/**
 * Le traducteur de l'écran courant.
 *
 * Rend une FONCTION plutôt qu'un objet figé : un objet obligerait chaque
 * composant à se souvenir de le régénérer quand la formule OU la langue
 * change, et l'oubli afficherait le vocabulaire ou la langue de l'autre
 * produit sans erreur visible.
 */
export function useMots(): (cle: CleMot) => string {
  const formule = useFormule()
  const lang = useLang()
  return (cle) => mot(cle, formule, lang)
}
